import React from "react";
import { ArrowRight, Sparkles, Star } from "lucide-react";
import { Button } from "../ui/button";
import { STATS } from "../../mock";

export default function Hero() {
  return (
    <section
      id="top"
      className="relative pt-32 lg:pt-40 pb-20 lg:pb-28 overflow-hidden noise-overlay"
    >
      {/* Background grid + soft accents */}
      <div className="absolute inset-0 grid-bg opacity-60" />
      <div className="absolute -top-20 -left-20 h-72 w-72 rounded-full bg-sky-200 blur-3xl float-slow opacity-70" />
      <div className="absolute top-40 right-0 h-80 w-80 rounded-full bg-blue-100 blur-3xl float-slow opacity-80" />
      <div className="absolute bottom-0 left-1/3 h-64 w-64 rounded-full bg-sky-100 blur-3xl float-slow opacity-60" />

      <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
        <div className="grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7 fade-up">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white border border-sky-200 shadow-sm text-xs text-slate-700">
              <Sparkles className="h-3.5 w-3.5 text-sky-500" />
              Lanarkshire-based · Available for new projects
            </div>

            <h1 className="mt-6 text-4xl sm:text-5xl lg:text-7xl font-semibold tracking-tight text-slate-900 leading-[1.02]">
              Web Design Hamilton &amp; Motherwell<br className="hidden md:block" />
              <span className="text-gradient-accent"> plus social media marketing that grows your business.</span>
            </h1>

            <p className="mt-6 text-lg text-slate-600 max-w-xl leading-relaxed">
              Your local web designer and social media manager in Hamilton and
              Motherwell. Websites from £99 for businesses in Wishaw, Bothwell,
              Blantyre, Bellshill, East Kilbride, Larkhall and every town within
              10 miles.
            </p>

            <div className="mt-8 flex flex-wrap items-center gap-3">
              <Button
                asChild
                className="bg-slate-900 hover:bg-slate-800 text-white rounded-full px-6 h-12 group"
              >
                <a href="#contact">
                  Start a project
                  <ArrowRight className="ml-1.5 h-4 w-4 btn-arrow" />
                </a>
              </Button>
              <Button
                asChild
                variant="outline"
                className="rounded-full px-6 h-12 border-slate-300 hover:bg-slate-50"
              >
                <a href="#work">See our work</a>
              </Button>

              <div className="flex items-center gap-2 ml-2 text-sm text-slate-600">
                <div className="flex -space-x-2">
                  {[
                    "https://images.unsplash.com/photo-1494790108377-be9c29b29330?auto=format&fit=crop&w=80&q=80",
                    "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?auto=format&fit=crop&w=80&q=80",
                    "https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?auto=format&fit=crop&w=80&q=80",
                  ].map((src, i) => (
                    <img
                      key={i}
                      src={src}
                      alt="client"
                      className="h-7 w-7 rounded-full border-2 border-white object-cover"
                    />
                  ))}
                </div>
                <div className="flex items-center gap-1">
                  <div className="flex">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <Star
                        key={i}
                        className="h-3.5 w-3.5 fill-amber-400 text-amber-400"
                      />
                    ))}
                  </div>
                  <span className="text-slate-700 font-medium">5.0</span>
                  <span>from 40+ clients</span>
                </div>
              </div>
            </div>

            <div className="mt-14 grid grid-cols-2 sm:grid-cols-4 gap-6 max-w-2xl">
              {STATS.map((s) => (
                <div key={s.label}>
                  <div className="text-2xl lg:text-3xl font-semibold text-slate-900">
                    {s.value}
                  </div>
                  <div className="text-xs text-slate-500 mt-1 leading-tight">
                    {s.label}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Right visual */}
          <div className="lg:col-span-5 relative fade-up">
            <div className="relative aspect-[4/5] rounded-3xl overflow-hidden border border-slate-200 shadow-2xl shadow-slate-900/10">
              <img
                src="https://images.unsplash.com/photo-1515879218367-8466d910aaa4?auto=format&fit=crop&w=1200&q=80"
                alt="Web design and software development workspace in Lanarkshire"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-slate-900/40 via-transparent to-transparent" />
              <div className="absolute bottom-5 left-5 right-5 bg-white/95 backdrop-blur rounded-2xl p-4 border border-slate-200">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-[11px] uppercase tracking-wider text-slate-500">
                      Currently building
                    </div>
                    <div className="text-sm font-semibold text-slate-900 mt-0.5">
                      AI-powered booking platform
                    </div>
                  </div>
                  <div className="h-2.5 w-2.5 rounded-full bg-emerald-500 animate-pulse" />
                </div>
              </div>
            </div>

            {/* Floating card */}
            <div className="hidden md:block absolute -left-8 -bottom-8 bg-white rounded-2xl border border-slate-200 shadow-xl p-4 w-56">
              <div className="text-xs text-slate-500">Lighthouse score</div>
              <div className="flex items-end gap-2 mt-1">
                <div className="text-3xl font-semibold text-slate-900">98</div>
                <div className="text-xs text-emerald-600 mb-1.5">
                  +27 vs avg
                </div>
              </div>
              <div className="mt-3 h-1.5 rounded-full bg-slate-100 overflow-hidden">
                <div className="h-full w-[98%] bg-emerald-500" />
              </div>
            </div>

            <div className="hidden md:block absolute -right-6 top-10 bg-white rounded-2xl border border-slate-200 shadow-xl p-4">
              <div className="flex items-center gap-2">
                <div className="h-8 w-8 rounded-lg bg-sky-50 flex items-center justify-center">
                  <Sparkles className="h-4 w-4 text-sky-600" />
                </div>
                <div>
                  <div className="text-[11px] text-slate-500">AI assistant</div>
                  <div className="text-sm font-medium text-slate-900">
                    Online · 24/7
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
